import React, { useMemo, useState, useEffect, useRef } from "react";
import { Download, Search, ChevronUp, ChevronDown } from "lucide-react";
import { fetchTransactions } from "../api";

export default function PaymentHistory() {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "date", direction: "desc" });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const isMounted = useRef(true);

  const loadTransactions = () => {
    setLoading(true);
    fetchTransactions()
      .then(res => {
        if (!isMounted.current) return;
        const rawList = 
          res.data?.transactions || 
          res.data?.history || 
          res.history || 
          res.transactions || 
          (Array.isArray(res.data) ? res.data : []) || 
          (Array.isArray(res) ? res : []);
        const mappedTransactions = rawList.filter(Boolean).map(t => {
          const isCreditHistory = !!t.usage_id || !!t.amount_consumed || !!t.tryon_category;
          
          let title = '';
          let billingCycle = '';
          let date = 'N/A';
          let planExpiryDate = 'N/A';
          let invoice = 'N/A';
          let status = 'PAID';
          let statusColor = 'green';
          let amount = 'N/A';

          // Format Date
          const rawDate = t.created_at || t.createdAt || t.created;
          let dateObj = null;
          if (rawDate) {
            if (typeof rawDate === 'number' || /^\d+$/.test(rawDate)) {
              const numDate = parseInt(rawDate, 10);
              if (numDate < 9999999999) {
                dateObj = new Date(numDate * 1000);
              } else {
                dateObj = new Date(numDate);
              }
            } else {
              dateObj = new Date(rawDate);
            }
          }
          if (dateObj && !isNaN(dateObj.getTime())) {
            date = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
          }

          if (isCreditHistory) {
            // Mapping for credit history logs
            const category = t.tryon_category ? t.tryon_category.charAt(0).toUpperCase() + t.tryon_category.slice(1) : 'TryOn';
            title = `TryOn (${category})`;
            billingCycle = t.credit_source || 'Personal';
            
            invoice = t.usage_id || 'N/A';
            status = t.outcome || 'SUCCESS';
            statusColor = status.toUpperCase() === 'SUCCESS' ? 'green' : status.toUpperCase() === 'REFUNDED' ? 'blue' : 'gray';
            
            const consumed = typeof t.amount_consumed === 'number' ? t.amount_consumed : parseInt(t.amount_consumed, 10) || 0;
            amount = status.toUpperCase() === 'REFUNDED' ? `+${consumed} TryOn` : `-${consumed} TryOn`;
            planExpiryDate = t.refund_reason || 'N/A';
          } else {
            // Traditional payment transaction mapping
            let desc = t.plan_name || t.description;
            if (!desc && t.plan_id) {
              const pid = t.plan_id.replace('plan_', '');
              desc = pid.charAt(0).toUpperCase() + pid.slice(1) + " Plan";
            }
            if (!desc) desc = 'Starter Plan';

            const titleMatch = desc.match(/([a-zA-Z0-9\s\-]+)\sPlan/i) || desc.match(/([a-zA-Z0-9\s\-]+)/i);
            title = titleMatch ? titleMatch[1] : desc;
            
            billingCycle = desc.toLowerCase().includes('yearly') ? 'Yearly' : desc.toLowerCase().includes('one-time') ? 'One-time' : 'Monthly';
            
            if (dateObj && !isNaN(dateObj.getTime())) {
              const planExpiryObj = new Date(dateObj);
              if (billingCycle === 'Yearly') {
                planExpiryObj.setFullYear(planExpiryObj.getFullYear() + 1);
              } else if (billingCycle === 'One-time') {
                planExpiryObj.setDate(planExpiryObj.getDate() + 2); // e.g. 2-Day Pass
              } else {
                planExpiryObj.setMonth(planExpiryObj.getMonth() + 1);
              }
              planExpiryDate = planExpiryObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            }

            invoice = t.transaction_id || t.invoice_id || t.invoiceId || t.invoice || 'N/A';
            
            const currencySymbol = (t.currency || '').toUpperCase() === 'INR' ? '₹' : (t.currency || '').toUpperCase();
            let parsedAmount = typeof t.amount === 'number' ? t.amount : parseFloat(t.amount) || 0;
            let finalAmount = parsedAmount;
            if (parsedAmount > 100 && Number.isInteger(parsedAmount)) {
              finalAmount = parsedAmount / 100;
            }
            amount = `${currencySymbol}${finalAmount.toLocaleString()}`;
            status = t.status || 'PAID';
            statusColor = status.toUpperCase() === 'PAID' ? 'green' : 'gray';
          }

          return {
            title,
            billingCycle,
            date,
            planExpiryDate,
            invoice,
            status,
            statusColor,
            amount
          };
        });
        setTransactions(mappedTransactions);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        if (isMounted.current) setLoading(false);
      });
  };

  useEffect(() => {
    isMounted.current = true;
    loadTransactions();
    return () => { isMounted.current = false; };
  }, []);

  const downloadInvoice = (transaction) => {
    const invoiceContent = `MIGGUS - INVOICE
======================================
Invoice / Txn ID : ${transaction.invoice || 'N/A'}
Plan Name        : ${transaction.title || 'N/A'} Plan
Billing Cycle    : ${transaction.billingCycle || 'N/A'}
Transaction Date : ${transaction.date || 'N/A'}
Plan Expiry Date : ${transaction.planExpiryDate || 'N/A'}
Amount Paid      : ${transaction.amount || 'N/A'}
Payment Status   : ${(transaction.status || 'paid').toUpperCase()}

======================================
Thank you for your business with Miggus!
If you have any questions, contact billing@miggus.com.`;

    const blob = new Blob([invoiceContent], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Invoice-${transaction.invoice || 'Transaction'}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const filteredTransactions = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    if (!normalizedQuery) {
      return transactions;
    }

    return transactions.filter((transaction) => {
      return transaction.title.toLowerCase().includes(normalizedQuery);
    });
  }, [searchQuery, transactions]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedTransactions = useMemo(() => {
    const sortableItems = [...filteredTransactions];
    sortableItems.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === "date" || sortConfig.key === "planExpiryDate") {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else if (sortConfig.key === "amount") {
        aValue = parseInt(a.amount.replace(/[^\d-]/g, ""), 10);
        bValue = parseInt(b.amount.replace(/[^\d-]/g, ""), 10);
      } else {
        aValue = aValue.toString().toLowerCase();
        bValue = bValue.toString().toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    return sortableItems;
  }, [filteredTransactions, sortConfig]);

  const SortHeader = ({ label, sortKey, align = "left" }) => {
    const isActive = sortConfig?.key === sortKey;
    const isAsc = isActive && sortConfig.direction === "asc";
    const justifyClass =
      align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start";

    return (
      <div
        className={`flex items-center gap-1.5 cursor-pointer hover:text-gray-600 transition-colors select-none group ${justifyClass}`}
        onClick={() => requestSort(sortKey)}
      >
        {label}
        {isActive ? (
          isAsc ? (
            <ChevronUp className="w-[14px] h-[14px] text-[#8c3af0]" strokeWidth={3} />
          ) : (
            <ChevronDown className="w-[14px] h-[14px] text-[#8c3af0]" strokeWidth={3} />
          )
        ) : (
          <ChevronDown className="w-[14px] h-[14px] text-gray-300 group-hover:text-gray-400 transition-colors" strokeWidth={3} />
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="flex-1 flex items-center justify-center"><div className="text-[#8c3af0] font-medium animate-pulse">Loading history...</div></div>;
  }

  return (
    <div className="flex-1 overflow-y-auto px-5 lg:px-10 pb-12">
      <div className="max-w-[1050px] mx-auto pt-4 lg:pt-6 space-y-6">
        <div>
          <div className="relative">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-300 w-[18px] h-[18px] stroke-[2.5]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by plan..."
              className="w-full bg-white rounded-full py-[14px] pl-[46px] pr-6 text-[15px] shadow-sm border border-transparent focus:border-purple-300 focus:ring-2 focus:ring-purple-100 outline-none transition-all placeholder:text-gray-300 text-gray-700"
            />
          </div>
        </div>
        <div className="hidden md:block bg-white rounded-[24px] shadow-sm border border-gray-100/50 pt-2 overflow-hidden relative z-0">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[1000px]">
              <thead>
                <tr className="border-b border-gray-100/70">
                  <th className="py-4 px-6 lg:px-8 font-bold text-[11px] text-gray-400 tracking-[0.15em] uppercase w-[20%]">
                    <SortHeader label="Plan" sortKey="title" />
                  </th>
                  <th className="py-4 px-4 font-bold text-[11px] text-gray-400 tracking-[0.15em] uppercase w-[11%]">
                    <SortHeader label="Cycle" sortKey="billingCycle" />
                  </th>
                  <th className="py-4 px-4 font-bold text-[11px] text-gray-400 tracking-[0.15em] uppercase w-[15%]">
                    <SortHeader label="Transaction Date" sortKey="date" />
                  </th>
                  <th className="py-4 px-4 font-bold text-[11px] text-gray-400 tracking-[0.15em] uppercase w-[16%]">
                    <SortHeader label="Plan Expiry Date" sortKey="planExpiryDate" />
                  </th>
                  <th className="py-4 px-4 font-bold text-[11px] text-gray-400 tracking-[0.15em] uppercase w-[20%]">
                    <SortHeader label="Invoice" sortKey="invoice" />
                  </th>
                  <th className="py-4 px-4 font-bold text-[11px] text-gray-400 tracking-[0.15em] uppercase w-[8%] text-center">
                    <SortHeader label="Amount" sortKey="amount" align="center" />
                  </th>
                  <th className="py-4 px-6 lg:px-8 font-bold text-[11px] text-gray-400 tracking-[0.15em] uppercase w-[10%] text-right">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedTransactions.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="py-12 text-center text-gray-400 font-medium">
                      No transactions match your search.
                    </td>
                  </tr>
                ) : (
                  sortedTransactions.map((transaction, index) => (
                    <tr
                      key={`${transaction.invoice}-${index}`}
                      className={`hover:bg-gray-50/30 transition-colors ${
                        index !== sortedTransactions.length - 1 ? "border-b border-gray-50" : ""
                      }`}
                    >
                      <td className="py-4 px-6 lg:px-8">
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className={`w-[42px] h-[42px] rounded-[12px] flex items-center justify-center text-[16px] font-bold shrink-0 shadow-sm ${
                              transaction.title === "Enterprise"
                                ? "bg-amber-100 text-amber-700"
                                : transaction.title === "Starter"
                                ? "bg-slate-100 text-slate-700"
                                : "bg-purple-100 text-purple-700"
                            }`}
                          >
                            {transaction.title.charAt(0)}
                          </div>
                          <div className="min-w-0 text-[14.5px] font-medium text-gray-800 truncate">
                            {transaction.title}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-[13px] font-medium text-gray-600">
                        {transaction.billingCycle}
                      </td>
                      <td className="py-4 px-4 text-[14px] font-mono text-gray-500">
                        {transaction.date}
                      </td>
                      <td className="py-4 px-4 text-[14px] font-mono text-gray-500">
                        {transaction.planExpiryDate}
                      </td>
                      <td className="py-4 px-4 text-[13.5px] font-mono text-gray-500">
                        {transaction.invoice}
                      </td>
                      <td className={`py-4 px-4 text-center text-[15px] font-bold ${transaction.amountColor || "text-gray-900"}`}>
                        {transaction.amount}
                      </td>
                      <td className="py-4 px-6 lg:px-8 text-right">
                        <button onClick={() => downloadInvoice(transaction)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-bold text-[#8c3af0] bg-purple-50 hover:bg-purple-100 transition-all whitespace-nowrap">
                          <Download className="w-3.5 h-3.5" />
                          Invoice
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile View */}
        <div className="md:hidden space-y-4">
          {sortedTransactions.length === 0 ? (
            <div className="py-12 text-center text-gray-400 font-medium bg-white rounded-[24px]">
              No transactions match your search.
            </div>
          ) : (
            sortedTransactions.map((transaction, index) => (
              <div
                key={`mobile-${transaction.invoice}-${index}`}
                className="bg-white rounded-[20px] p-[20px] shadow-sm border border-gray-100/50 flex flex-col gap-4"
              >
                {/* Top Row: Icon, Title & Cycle, Amount */}
                <div className="flex justify-between items-start">
                  <div className="flex gap-3 items-center">
                    <div
                      className={`w-[42px] h-[42px] rounded-[12px] flex items-center justify-center text-[16px] font-bold shrink-0 ${
                        transaction.title === "Enterprise"
                          ? "bg-amber-100 text-amber-700"
                          : transaction.title === "Starter"
                          ? "bg-slate-100 text-slate-700"
                          : "bg-purple-100 text-[#8c3af0]"
                      }`}
                    >
                      {transaction.title.charAt(0)}
                    </div>
                    <div>
                      <div className="text-[15.5px] font-bold text-gray-900 leading-tight">
                        {transaction.title}
                      </div>
                      <div className="text-[13px] text-gray-400 mt-[2px]">
                        {transaction.billingCycle}
                      </div>
                    </div>
                  </div>
                  <div
                    className={`text-[16px] font-bold ${
                      transaction.amountColor || "text-gray-900"
                    }`}
                  >
                    {transaction.amount}
                  </div>
                </div>

                {/* Middle Row: Dates */}
                <div className="flex justify-between mt-1">
                  <div className="flex flex-col gap-1">
                    <span className="text-[12px] text-gray-400">Transaction Date</span>
                    <span className="text-[13.5px] font-medium text-gray-800">
                      {transaction.date}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1 items-end text-right">
                    <span className="text-[12px] text-gray-400">Expiry Date</span>
                    <span className="text-[13.5px] font-medium text-gray-800">
                      {transaction.planExpiryDate}
                    </span>
                  </div>
                </div>

                {/* Bottom Row: Invoice ID and Download */}
                <div className="flex justify-between items-center mt-3 pt-2">
                  <span className="text-[12.5px] font-mono text-gray-400 tracking-wide">
                    {transaction.invoice}
                  </span>
                  <button onClick={() => downloadInvoice(transaction)} className="inline-flex items-center gap-1.5 text-[14px] font-medium text-[#8c3af0] hover:text-purple-700 transition-colors">
                    <Download className="w-[14px] h-[14px]" strokeWidth={2.5} />
                    Invoice
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
